import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PerformanceReports from './PerformanceTab/PerformanceReports';
import QualityReports from '../BiReports/QualityReportsTab/QualityReports';
import EfficiencyReports from './EfficiencyTab/EfficiencyReports';
import MiscReports from './MiscReportsComp/MiscReports';
import AttendanceReports from './AttendanceTab/AttendanceReports';
import RevenueTab from './RevenueTabComp/RevenueTab';
import HorizontalFilterBar from './HorizontalFilterBar';

const MainReports = () => {
  const tabs = ['performance', 'quality', 'efficiency', 'misc', 'attendance', 'revenue'];
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const initialTabIndex = tabs.indexOf(initialTab) >= 0 ? tabs.indexOf(initialTab) : 0;

  const [currentTabIndex, setCurrentTabIndex] = useState(initialTabIndex);
  const defaultFilters = {
    client: '',
    scope: '',
    user: '',
    startDate: '',
    endDate: '',
    monthYear: '',
  };

  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem('mainReportsFilters');
    const parsed = saved ? JSON.parse(saved) : {};
    return { ...defaultFilters, ...parsed };
  });

  const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  const handleFilterChange = (updatedFilters) => {
    const fullFilters = { ...defaultFilters, ...updatedFilters };
    if (!updatedFilters.monthYear) {
      fullFilters.monthYear = '';
    }
    if (isEqual(filters, fullFilters)) return;
    setFilters(fullFilters);
    localStorage.setItem('mainReportsFilters', JSON.stringify(fullFilters));
  };

  const handlePrev = () => {
    if (currentTabIndex > 0) {
      const newIndex = currentTabIndex - 1;
      setCurrentTabIndex(newIndex);
      setSearchParams({ tab: tabs[newIndex] });
    }
  };

  const handleNext = () => {
    if (currentTabIndex < tabs.length - 1) {
      const newIndex = currentTabIndex + 1;
      setCurrentTabIndex(newIndex);
      setSearchParams({ tab: tabs[newIndex] });
    }
  };

  const tabTitleMap = {
    performance: 'Performance Reports',
    quality: 'Quality Reports',
    efficiency: 'Efficiency Reports',
    misc: 'Misc Reports',
    attendance: 'Attendance Reports',
    revenue: 'Revenue Reports',
  };

  useEffect(() => {
    setSearchParams({ tab: tabs[currentTabIndex] });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentTabIndex]);

  return (
    <div className="relative flex flex-col min-h-screen bg-gray-100">
      <div className="sticky top-20 z-10 bg-gradient-to-r from-[#1A2B5C] to-[#EE1D23] shadow-xl rounded-b-2xl">
        <div className="flex flex-col md:flex-row items-center justify-between px-6 py-5 text-white">
          <button
            onClick={handlePrev}
            disabled={currentTabIndex === 0}
            className={`rounded-full p-3 shadow-md transition-all duration-300 ${currentTabIndex === 0 ? 'bg-white/30 text-white cursor-not-allowed' : 'bg-white text-[#EE1D23] hover:bg-gray-100'}`}
            title="Previous Tab"
          >
            <span className="text-xl">&#8592;</span>
          </button>

          <div className="text-2xl font-extrabold tracking-widest uppercase text-center px-4">
            {tabTitleMap[tabs[currentTabIndex]]}
            <p className="text-xs font-medium tracking-wide mt-1">
              Tab {currentTabIndex + 1} of {tabs.length}
            </p>
          </div>

          <button
            onClick={handleNext}
            disabled={currentTabIndex === tabs.length - 1}
            className={`rounded-full p-3 shadow-md transition-all duration-300 ${currentTabIndex === tabs.length - 1 ? 'bg-white/30 text-white cursor-not-allowed' : 'bg-white text-[#1A2B5C] hover:bg-gray-100'}`}
            title="Next Tab"
          >
            <span className="text-xl">&#8594;</span>
          </button>
        </div>
        <div className="px-4 pb-4">
          <HorizontalFilterBar filters={filters} onFilterChange={handleFilterChange} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 no-scrollbar">
        <div className="bg-white p-6 rounded-lg shadow-md min-h-[70vh]">
          {tabs[currentTabIndex] === 'performance' && <PerformanceReports filters={filters} />}
          {tabs[currentTabIndex] === 'quality' && <QualityReports filters={filters} />}
          {tabs[currentTabIndex] === 'efficiency' && <EfficiencyReports filters={filters} />}
          {tabs[currentTabIndex] === 'misc' && <MiscReports filters={filters} />}
          {tabs[currentTabIndex] === 'attendance' && <AttendanceReports filters={filters} />}
          {tabs[currentTabIndex] === 'revenue' && <RevenueTab filters={filters} />}
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default MainReports;
